import { computed } from 'vue'
import { ensureAuthSession, useAuthSession } from '@/modules/auth/public'
import { useCartSummary, bootstrapCartOnAppMount } from '@/modules/order/public'
import { useFavoritesSummary } from '@/modules/profile/public'
import {
  useAppNotifications,
  useFeatureFlags,
  useSnackbar,
  useRealTimeNotifications,
  useMercureConnection,
} from '@/modules/common/public'

const UNSYNCED_CART_WARNED_KEY = 'cart_unsynced_warned'

/**
 * Текст предупреждения о позициях гостевой корзины, которые сервер не принял после входа.
 *
 * Позиции не удалены: они остаются в localStorage до явного выхода, поэтому в тексте
 * причина от сервера (например, минимальный заказ), а не «товар больше недоступен».
 * Один и тот же набор показываем один раз за сессию, а не на каждой загрузке страницы.
 */
export function describeUnsyncedCartItems(
  failedItems: ReadonlyArray<{ productId: number | string; name: string }>,
  warnings: ReadonlyArray<string>,
): string | null {
  if (failedItems.length === 0) {
    return null
  }

  const signature = JSON.stringify([failedItems.map((item) => item.productId), warnings])
  try {
    if (sessionStorage.getItem(UNSYNCED_CART_WARNED_KEY) === signature) {
      return null
    }
    sessionStorage.setItem(UNSYNCED_CART_WARNED_KEY, signature)
  } catch {
    // Без sessionStorage просто показываем предупреждение
  }

  const reasons = [...new Set(warnings)].join('; ')
  const names = failedItems.map((item) => `«${item.name}»`).join(', ')
  const head =
    failedItems.length === 1
      ? `Товар ${names} не перенесён в корзину`
      : `Товары ${names} не перенесены в корзину`

  return reasons === '' ? head : `${head}: ${reasons}`
}

export function useAppShell() {
  const auth = useAuthSession()
  const cart = useCartSummary()
  const favorites = useFavoritesSummary()
  const notifications = useAppNotifications()
  const featureFlags = useFeatureFlags()
  const { unreadCount: unreadNotificationsCount } = useRealTimeNotifications()
  const { initialize: initializeWebSocket } = useMercureConnection()
  const { snackbar, hideSnackbar } = useSnackbar()

  const isProcurementEnabled = computed(() => featureFlags.isEnabled('procurement_enabled'))
  const isSupplierShowcaseEnabled = computed(() =>
    featureFlags.isEnabled('supplier_showcase_enabled'),
  )
  const isChatEnabled = computed(() => featureFlags.isEnabled('chat_enabled'))

  const latestNotification = computed(() => notifications.notifications.value[0] ?? null)
  const showNotification = computed(() => latestNotification.value !== null)
  const notificationMessage = computed(() => latestNotification.value?.message ?? '')
  const notificationColor = computed(() => {
    const type = latestNotification.value?.type
    return type === 'success' || type === 'error' || type === 'warning' ? type : 'info'
  })
  const notificationActionLabel = computed(() => latestNotification.value?.action?.label)

  async function initializeShellData(isAuthenticated: boolean) {
    // Wait for cookie refresh before deciding server vs guest cart.
    const authStore = await ensureAuthSession({})
    const authenticated = isAuthenticated || authStore.isAuthenticated

    const cartInitResult = await bootstrapCartOnAppMount()
    const cartWarning = describeUnsyncedCartItems(
      cartInitResult.failedItems,
      cartInitResult.warnings,
    )
    if (cartWarning !== null) {
      notifications.showWarning(cartWarning)
    }

    if (authenticated) {
      await initializeWebSocket().catch(console.error)
    }
  }

  function dismissNotification(value: boolean) {
    if (!value && latestNotification.value) {
      notifications.removeNotification(latestNotification.value.id)
    }
  }

  function onNotificationAction(routerPush: (to: string) => void) {
    const to = latestNotification.value?.action?.to
    if (to) routerPush(to)
    dismissNotification(false)
  }

  return {
    auth,
    cart,
    favorites,
    featureFlags,
    snackbar,
    hideSnackbar,
    unreadNotificationsCount,
    isProcurementEnabled,
    isSupplierShowcaseEnabled,
    isChatEnabled,
    latestNotification,
    showNotification,
    notificationMessage,
    notificationColor,
    notificationActionLabel,
    initializeShellData,
    dismissNotification,
    onNotificationAction,
  }
}
